import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { initiatePasswordReset } from '@/lib/api/auth';
import { ApiError } from '@/lib/api';
import { Button, Card, CardTitle } from '@/components/ui';
import { Input } from '@/components/ui/Input';

/**
 * ForgotPassword — solicita el reset vía `POST /v1/auth/password-reset/initiate`
 * (api-core). El email contiene un link a `/reset-password?token=<token>`.
 */
export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await initiatePasswordReset(email);
      // Respuesta uniforme aunque el email no exista (no se filtra si la
      // cuenta existe — defensa contra enumeración).
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos enviar el correo.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="container-fc py-12">
      <div className="mx-auto max-w-md">
        <Card variant="default" padded>
          <CardTitle>Recuperar contraseña</CardTitle>
          {sent ? (
            <>
              <p className="mt-3 text-sm text-fc-ink" role="status">
                Te enviamos un correo a <strong>{email}</strong> con instrucciones para reestablecer
                tu contraseña. Revisá tu bandeja de entrada y la carpeta de spam.
              </p>
              <div className="mt-6">
                <Button as={Link} to="/login" variant="secondary">
                  Volver a iniciar sesión
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-fc-muted">
                Ingresá el email asociado a tu cuenta. Te mandamos un link para definir una
                contraseña nueva.
              </p>
              <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
                <Input
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {error && (
                  <p role="alert" className="text-sm text-danger">
                    {error}
                  </p>
                )}
                <Button type="submit" disabled={pending}>
                  {pending ? 'Enviando…' : 'Enviar link de recuperación'}
                </Button>
                <p className="text-sm text-fc-muted">
                  ¿Te acordaste de tu contraseña?{' '}
                  <Link to="/login" className="underline hover:text-fc-ink">
                    Iniciar sesión
                  </Link>
                </p>
              </form>
            </>
          )}
        </Card>
      </div>
    </section>
  );
}
